import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X, Package, Check } from 'lucide-react';

import { useProducts } from '@/features/products/hooks/use-products';
import type { Product } from '@/lib/types';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Skeleton } from '@/components/atoms/skeleton';
import { Badge } from '@/components/atoms/badge';
import { getImageUrl, formatPrice } from '@/lib/utils';
import { useConfigStore } from '@/lib/stores/config.store';

export interface SelectedProduct {
  refId: string;
  name: string;
  image?: string;
  price?: number;
}

interface ProductPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when confirming selection (single mode) */
  onSelect?: (product: SelectedProduct) => void;
  /** Called when confirming selection (multi mode) */
  onSelectMultiple?: (products: SelectedProduct[]) => void;
  /** Pre-selected product ID (single mode) */
  selectedProductId?: string;
  /** Pre-selected product IDs (multi mode) */
  selectedProductIds?: string[];
  /** Enable multi-select mode */
  multiSelect?: boolean;
  title?: string;
  description?: string;
}

export function ProductPickerDialog({
  open,
  onOpenChange,
  onSelect,
  onSelectMultiple,
  selectedProductId,
  selectedProductIds = [],
  multiSelect = false,
  title,
  description,
}: ProductPickerDialogProps) {
  const { t } = useTranslation();
  const [search, setSearch] = React.useState('');
  const [internalSelected, setInternalSelected] = React.useState<string[]>([]);
  const { currency } = useConfigStore();

  const { data: productsData, isLoading } = useProducts({ search });
  const products = productsData?.products ?? [];

  // Track previous open state to detect dialog opening/closing
  const prevOpenRef = React.useRef(open);

  // Sync selection only when dialog opens, reset search when closes
  React.useEffect(() => {
    // Dialog is opening (was closed, now open) - sync selection from props
    if (open && !prevOpenRef.current) {
      if (multiSelect) {
        setInternalSelected(selectedProductIds);
      } else {
        setInternalSelected(selectedProductId ? [selectedProductId] : []);
      }
    }
    // Dialog is closing - reset search
    if (!open && prevOpenRef.current) {
      setSearch('');
    }
    prevOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]); // Only trigger on open change, read other props directly

  const handleSelect = (product: Product) => {
    const productId = product.refId || String(product.id);

    if (multiSelect) {
      // Toggle selection in multi-select mode
      setInternalSelected((prev) =>
        prev.includes(productId)
          ? prev.filter((id) => id !== productId)
          : [...prev, productId]
      );
    } else {
      // Replace selection in single mode
      setInternalSelected([productId]);
    }
  };

  const isProductSelected = (product: Product): boolean => {
    const productId = product.refId || String(product.id);
    return internalSelected.includes(productId);
  };

  const handleConfirm = () => {
    const selectedProducts = products.filter((p) =>
      internalSelected.includes(p.refId || String(p.id))
    );

    if (selectedProducts.length === 0) return;

    const mappedProducts: SelectedProduct[] = selectedProducts.map((p) => ({
      refId: p.refId || String(p.id),
      name: p.name,
      image: getImageUrl(p.images?.[0]) || undefined,
      price: p.prices?.euro ? parseFloat(p.prices.euro) : p.price,
    }));

    if (multiSelect && onSelectMultiple) {
      onSelectMultiple(mappedProducts);
    } else if (onSelect && mappedProducts.length > 0) {
      onSelect(mappedProducts[0]);
    }

    onOpenChange(false);
  };

  const getProductPrice = (product: Product): string => {
    if (product.prices) {
      if (currency === 'EUR' && product.prices.euro) {
        return formatPrice(parseFloat(product.prices.euro), 'EUR');
      }
      if (currency === 'USD' && product.prices.usd) {
        return formatPrice(parseFloat(product.prices.usd), 'USD');
      }
      if (currency === 'KMF' && product.prices.comoros) {
        return formatPrice(parseFloat(product.prices.comoros), 'KMF');
      }
      // Default to EUR
      if (product.prices.euro) {
        return formatPrice(parseFloat(product.prices.euro), 'EUR');
      }
    }
    return formatPrice(product.price, 'EUR');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {title || t('ads.productPicker.title', 'Sélectionner un produit')}
          </DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            placeholder={t('ads.productPicker.searchPlaceholder', 'Rechercher un produit...')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-9"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          )}
        </div>

        {/* Selected indicator */}
        {internalSelected.length > 0 && (
          <div className="text-sm text-muted-foreground">
            <Badge variant="secondary">
              {internalSelected.length === 1
                ? t('ads.productPicker.productSelected', '1 produit sélectionné')
                : t('ads.productPicker.productsSelected', '{{count}} produits sélectionnés', { count: internalSelected.length })}
            </Badge>
          </div>
        )}

        {/* Products list */}
        <div className="flex-1 overflow-y-auto space-y-2 min-h-[300px] max-h-[400px]">
          {isLoading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg border">
                  <Skeleton className="size-14 rounded" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Package className="size-12 mb-3 opacity-50" />
              <p className="font-medium">
                {t('ads.productPicker.noProducts', 'Aucun produit trouvé')}
              </p>
              <p className="text-sm">
                {t('ads.productPicker.trySearch', 'Essayez une autre recherche')}
              </p>
            </div>
          ) : (
            products.map((product) => {
              const isSelected = isProductSelected(product);
              const imageUrl = getImageUrl(product.images?.[0]) || '';
              const productId = product.refId || String(product.id);

              return (
                <button
                  key={productId}
                  type="button"
                  onClick={() => handleSelect(product)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all ${
                    isSelected
                      ? 'border-primary bg-primary/5 ring-1 ring-primary'
                      : 'border-input hover:border-primary/50 hover:bg-muted/50'
                  }`}
                >
                  {/* Selection indicator - checkbox for multi, radio for single */}
                  <div
                    className={`size-5 ${multiSelect ? 'rounded' : 'rounded-full'} border-2 flex items-center justify-center shrink-0 transition-colors ${
                      isSelected
                        ? 'border-primary bg-primary'
                        : 'border-muted-foreground/30'
                    }`}
                  >
                    {isSelected && <Check className="size-3 text-white" />}
                  </div>

                  {/* Product image */}
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={product.name}
                      className="size-14 rounded object-cover shrink-0"
                    />
                  ) : (
                    <div className="size-14 rounded bg-muted flex items-center justify-center shrink-0">
                      <Package className="size-6 text-muted-foreground" />
                    </div>
                  )}

                  {/* Product info */}
                  <div className="flex-1 text-left min-w-0">
                    <p className="font-medium line-clamp-1">{product.name}</p>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>{getProductPrice(product)}</span>
                      {product.shopName && (
                        <>
                          <span>•</span>
                          <span className="truncate">{product.shopName}</span>
                        </>
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={internalSelected.length === 0}
          >
            {t('common.confirm', 'Confirmer')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Compact display for selected product
interface SelectedProductDisplayProps {
  product: SelectedProduct | null;
  onClear: () => void;
  onChangeClick: () => void;
}

export function SelectedProductDisplay({
  product,
  onClear,
  onChangeClick,
}: SelectedProductDisplayProps) {
  const { t } = useTranslation();

  if (!product) {
    return null;
  }

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
      {product.image ? (
        <img
          src={product.image}
          alt={product.name}
          className="size-12 rounded object-cover shrink-0"
        />
      ) : (
        <div className="size-12 rounded bg-muted flex items-center justify-center shrink-0">
          <Package className="size-5 text-muted-foreground" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="font-medium line-clamp-1">{product.name}</p>
        <p className="text-xs text-muted-foreground">{product.refId}</p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onChangeClick}
        >
          {t('common.change', 'Modifier')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8 text-muted-foreground hover:text-destructive"
          onClick={onClear}
        >
          <X className="size-4" />
        </Button>
      </div>
    </div>
  );
}
